import { createTheme } from "@mui/material";

export const BRAND_PRIMARY = "#1600EA";
export const BRAND_ACCENT = "#EAFF6C";
export const BRAND_TERTIARY = "#B7F9F7";

export const RADIUS_SM = 6; // buttons, chips, tooltips
export const RADIUS_MD = 8; // base (shape.borderRadius)
export const RADIUS_LG = 12; // cards

export const SHADOW_SM = 1; // subtle elevation
export const SHADOW_MD = 4; // cards, dropdowns
export const SHADOW_LG = 8; // modals, drawers

export const BRAND_ACCENT_LIGHT = "#EBFF94";
export const BRAND_ACCENT_HOVER = "#F4F993";

export const STATUS_COLORS = {
  error: { bg: "#FFEBEE", border: "#DC3545", text: "#C62828" },
  warning: { bg: "#FFF8E1", border: "#FFC107", text: "#EF6C00" },
  info: { bg: "#E3F2FD", border: "#17A2B8", text: "#1565C0" },
  default: { bg: "#F5F5F5", border: "#9E9E9E", text: "#616161" },
  success: { bg: "#E8F5E9", border: "#28A745", text: "#2E7D32" },
  purple: { bg: "#F3E5F5", border: "#9C27B0", text: "#6A1B9A" },
} as const;

export const SIDEBAR = {
  bg: "#F1F1F2",
  textActive: "#0D0D0F",
  textInactive: "#5A5A60",
  itemActiveBg: "#F8F8F8",
  divider: "#E9E9EA",
} as const;

export const CHART_COLORS = [
  "#8884D8",
  "#82CA9D",
  "#FFC658",
  "#FF8042",
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#A4DE6C",
] as const;

export const USAGE_COLORS: Record<string, string> = {
  test_suite_run: "#8884d8",
  test_run: "#82ca9d",
  "test_run.auto_fix": "#ffc658",
  "test_run.ai_step": "#ff7300",
  "generate.step": "#0088fe",
  "generate.step.max": "#00c49f",
  "generate.step.ultra": "#ffbb28",
  discovery_run: "#ff8042",
  scenario: "#a4de6c",
  search: "#d0ed57",
  "search.max": "#ffd700",
  "search.ultra": "#ff6b6b",
  test_chat: "#8dd1e1",
  raw_ai_query: "#b39ddb",
  "agent.stealth": "#f06292",
  deduction: "#4caf50",
  check_schedule_run: "#26a69a",
  "check.ai_validation": "#7986cb",
  "check.ai_validation.max": "#5c6bc0",
  "check.ai_validation.ultra": "#3f51b5",
} as const;

export const SEVERITY_COLORS = {
  critical: "#dc3545",
  high: "#ffc107",
  medium: "#e3c314",
  low: "#28a745",
} as const;

export const MARKDOWN_COLORS = {
  inlineCode: "#e11d48",
  inlineCodeBg: "#f1f5f9",
  inlineCodeBorder: "#e2e8f0",
  highlightBg: "#f2f2f2",
  text: "#2d2d2d",
  link: "#2563eb",
  tableBg: "#ffffff",
  tableHeaderBg: "#f8fafc",
  tableHeaderText: "#374151",
  tableBorder: "#e5e7eb",
  tableCellBorder: "#f3f4f6",
} as const;

export const theme = createTheme({
  cssVariables: true,
  palette: {
    primary: {
      main: BRAND_PRIMARY,
    },
    secondary: {
      main: BRAND_ACCENT,
    },
    tertiary: {
      main: BRAND_TERTIARY,
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#212529",
      secondary: "#6c757d",
    },
    success: {
      main: "#28a745",
    },
    error: {
      main: "#dc3545",
    },
    warning: {
      main: "#ffc107",
    },
    info: {
      main: "#17a2b8",
    },
  },
  typography: {
    fontFamily: ["Geist", "sans-serif"].join(","),
    h1: {
      fontSize: "clamp(2rem, 1.6rem + 2vw, 3rem)",
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: "clamp(1.75rem, 1.45rem + 1.5vw, 2.5rem)",
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h3: {
      fontSize: "clamp(1.5rem, 1.2rem + 1.5vw, 2rem)",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: "clamp(1.25rem, 1.1rem + 0.75vw, 1.5rem)",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.6,
    },
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.6,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: RADIUS_MD,
  },
  shadows: [
    "none",
    "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
    "0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
    "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
    "0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)",
    "0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)",
    "0 -1px 2px 0 rgba(0, 0, 0, 0.05)",
    "0 1px 1px 0 rgba(0, 0, 0, 0.1)",
    "0 2px 2px 0 rgba(0, 0, 0, 0.1)",
    "0 3px 3px 0 rgba(0, 0, 0, 0.1)",
    "0 4px 4px 0 rgba(0, 0, 0, 0.1)",
    "0 6px 8px 0 rgba(0, 0, 0, 0.1)",
    "0 8px 12px 0 rgba(0, 0, 0, 0.1)",
    "0 12px 16px 0 rgba(0, 0, 0, 0.1)",
    "0 16px 24px 0 rgba(0, 0, 0, 0.1)",
    "0 24px 32px 0 rgba(0, 0, 0, 0.1)",
    "0 1px 2px 0 rgba(0, 0, 0, 0.1)",
    "0 1px 3px 1px rgba(0, 0, 0, 0.15)",
    "0 1px 5px 2px rgba(0, 0, 0, 0.15)",
    "0 2px 7px 3px rgba(0, 0, 0, 0.15)",
    "0 3px 9px 4px rgba(0, 0, 0, 0.15)",
    "0 5px 12px 6px rgba(0, 0, 0, 0.15)",
    "0 8px 18px 8px rgba(0, 0, 0, 0.15)",
    "0 13px 28px 12px rgba(0, 0, 0, 0.15)",
    "0 20px 40px 18px rgba(0, 0, 0, 0.15)",
  ],
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        variant: "contained",
      },
      variants: [
        {
          props: { variant: "gradient" },
          style: {
            color: "#ffffff",
            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
              boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
            },
            "&:focus-visible": {
              outline: `2px solid ${BRAND_ACCENT}`,
              outlineOffset: "2px",
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: RADIUS_SM,
          padding: "8px 16px",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: RADIUS_LG,
          border: "1px solid rgba(0, 0, 0, 0.12)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS_SM,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#212529",
          color: "#ffffff",
          borderRadius: RADIUS_SM,
          padding: "8px 12px",
        },
        arrow: {
          color: "#212529",
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: "1600px !important",
        },
      },
    },
    // Reduce font size of navigation menu items (Toolpad Dashboard sidebar)
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: "0.9rem",
          lineHeight: 1.4,
        },
      },
    },
  },
});

// Augment the palette to include tertiary color
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    gradient: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    tertiary: Palette["primary"];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions["primary"];
  }
}
